import {
	ChangeEvent,
	Fragment,
	useEffect,
	useRef,
	RefObject,
	Dispatch,
	SetStateAction,
	useContext
} from 'react';
import {
	H3,
	H4,
	Spacer,
	Text,
	Tile,
	InfoChip,
	Caption,
	Grid,
	InputField
} from '@components';
import { EditShipment } from '@state';
import { useRecoilState } from 'recoil';
import { formatDate, groupCargosByStop } from '@utils';
import styles from '../EditShipment.module.scss';
import ArrowUpIcon from '@mui/icons-material/NorthEast';
import ArrowDownIcon from '@mui/icons-material/SouthEast';
import { CHIP_SIZE, TShipmentOrder, TPagedDialogContext, TEditShipmentErrors } from '@types';
import { EditShipmentContext } from '../EditShipment.Context';

type TProps = {
	pagesRef?: RefObject<HTMLElement>;
	context?: TPagedDialogContext;
	errors: TEditShipmentErrors | null;
	setErrors: Dispatch<SetStateAction<TEditShipmentErrors | null>>;
}

export const ContactDetails = (props: TProps) => {
	const { context, pagesRef, errors, setErrors } = props;
	const ctx = useContext(EditShipmentContext);
	const inputsDisabled = ctx?.access.pages.contactDetails.fields.inputs.disabled ?? false;
	const isScrollRef = useRef(-1);
	const subpages = useRef<(HTMLDivElement | null)[]>([]);
	const [shipment, setShipment] = useRecoilState(EditShipment);
	// Cargos collected from the same place are one stop, so an edit applies to every cargo on it.
	const { pickups: pickupGroups, dropoffs: dropoffGroups } = groupCargosByStop(shipment?.cargos ?? []);

	const updateVal = (e: ChangeEvent<HTMLInputElement>, dest: 'pickup' | 'dropoff', index: number) => {
		const { name, value } = e.target;
		const update = {
			...shipment,
			cargos: shipment?.cargos.map((cargo, i) => {
				if (i !== index) return cargo;
				return {
					...cargo,
					[dest]: {
						...cargo[dest],
						[name]: value
					}
				}
			})
		} as TShipmentOrder
		setShipment(update);
		if (ctx?.setTouched) ctx.setTouched(true);
	}
	const getDateTimes = (dest: 'pickup' | 'dropoff', index: number) => {
		const theDate = formatDate(new Date(shipment?.cargos[index][dest === 'pickup' ? 'pickup_at' : 'drop_at'] ?? new Date()), 'DATE_WITH_SHORT_MONTH_NAME');
		const timeFrom = dest === 'pickup' ? shipment?.cargos[index].pick_from : shipment?.cargos[0].drop_from;
		const timeTo = dest === 'pickup' ? shipment?.cargos[index].pick_to : shipment?.cargos[0].drop_to;
		return `${theDate} | ${timeFrom} - ${timeTo}`;
	}
	// The save sends one pick-up entry per stop but a single-entry drop-off array, so drop-off
	// errors always come back keyed on 0 regardless of the tile's render position.
	const getErrorKey = (dest: 'pickup' | 'dropoff', stopIndex: number) => dest === 'dropoff' ? '0' : `${stopIndex}`;
	const clearError = (dest: 'pickup' | 'dropoff', stopIndex: number, err: string) => {
		const errorKey = getErrorKey(dest, stopIndex);
		const updateErrors = errors ? {
			...errors,
			[dest]: {
				...errors[dest],
				[errorKey]: (errors[dest][errorKey] ?? []).filter((e) => e !== err)
			}
		} : null;
		setErrors(updateErrors);
	}
	/* Errors come back indexed by position in the pick-up array the save sent, which is one entry per
	   stop — so they key on the stop, while the field values read from the stop's first cargo. */
	const generateStopTile = (dest: 'pickup' | 'dropoff', index: number, stopIndex: number) => {
		const errArr = errors ? (errors[dest][getErrorKey(dest, stopIndex)] ?? []) : [];
		const cargo = shipment?.cargos[index];
		// The stop's own ordinal, falling back to render order for shipments booked before it was stored.
		const stopNumber = (dest === 'pickup' ? cargo?.pick_position : cargo?.drop_position) ?? stopIndex;
		return (
			<div ref={(el) => (subpages.current[stopIndex] = el)}>
				<Tile stretch>
					<div className={styles.row} style={{ justifyContent: 'space-between' }}>
						<Caption>Stop {stopNumber + 1}</Caption>
						<InfoChip text={dest === 'pickup' ? 'Pick-up' : 'Drop-off'} CustomIcon={dest === 'pickup' ? ArrowUpIcon : ArrowDownIcon} size={CHIP_SIZE.SMALL} />
					</div>
					<H4>{shipment?.cargos[index][dest === 'pickup' ? 'pickup' : 'dropoff'].address}</H4>
					<Text>{getDateTimes(dest, index)}</Text>
					<Spacer size={20} />
					<Grid columns={2}>
						<InputField
							name={'contact'}
							label={'Contact Name'}
							value={shipment?.cargos[index][dest].contact ?? ''}
							onChange={(e) => updateVal(e, dest, index)}
							keepErrorSpace={false}
							required
							disabled={inputsDisabled}
							hasError={errArr.some((err) => err === 'contact')}
							errorMessage={errArr.some((err) => err === 'contact') ? 'Contact is required' : undefined}
							onFocus={() => clearError(dest, stopIndex, 'contact')}
						/>
						<InputField
							name={'phone'}
							label={'Phone'}
							value={shipment?.cargos[index][dest].phone ?? ''}
							onChange={(e) => updateVal(e, dest, index)}
							keepErrorSpace={false}
							required
							disabled={inputsDisabled}
							hasError={errArr.some((err) => err === 'phone')}
							errorMessage={errArr.some((err) => err === 'phone') ? 'Phone is required' : undefined}
							onFocus={() => clearError(dest, stopIndex, 'phone')}
						/>
						<InputField
							name={'business_name'}
							label={'Business Name'}
							value={shipment?.cargos[index][dest].business_name ?? ''}
							onChange={(e) => updateVal(e, dest, index)}
							keepErrorSpace={false}
							required
							disabled={inputsDisabled}
							hasError={errArr.some((err) => err === 'business_name')}
							errorMessage={errArr.some((err) => err === 'business_name') ? 'Business Name is required' : undefined}
							onFocus={() => clearError(dest, stopIndex, 'business_name')}
						/>
						<InputField
							name={'email'}
							label={'Email'}
							value={shipment?.cargos[index][dest].email ?? ''}
							onChange={(e) => updateVal(e, dest, index)}
							keepErrorSpace={false}
							required
							disabled={inputsDisabled}
							hasError={errArr.some((err) => err === 'email')}
							errorMessage={errArr.some((err) => err === 'email') ? 'Email is required' : undefined}
							onFocus={() => clearError(dest, stopIndex, 'email')}
						/>
					</Grid>
				</Tile>
			</div>
		)
	}
	const onPagesScroll = () => {
		if (context && pagesRef && pagesRef.current && subpages) {
			const curPos = pagesRef.current.scrollTop;
			isScrollRef.current = curPos;
			setTimeout(() => {
				if (curPos !== isScrollRef.current) {
					isScrollRef.current = -1;
				}
			}, 100);
			subpages.current.forEach((sp, i) => {
				if (sp) {
					const threshold = 200;
					if (curPos < sp.offsetTop && curPos > sp.offsetTop - threshold) {
						context.setActiveSubMenu(i);
					}
				}
			});
		}
	}
	useEffect(() => {
		if (context && pagesRef && pagesRef.current && subpages) {
			const spTops = subpages.current.map((sp, i) => {
				if (i === 0) return 0;
				if (sp) return sp.offsetTop ?? 0;
				return 0;
			});
			if (isScrollRef.current < 0) {
				pagesRef.current?.scrollTo({
					top: context.activeSubMenu === 0 ? spTops[0] : spTops[context.activeSubMenu],
					behavior: 'smooth'
				});
			}
		}
		if (pagesRef && pagesRef.current) {
			pagesRef.current.addEventListener('scroll', onPagesScroll);
		}
		return () => {
			pagesRef && pagesRef.current && pagesRef.current.removeEventListener('scroll', onPagesScroll);
		}
	}, [context, pagesRef]);
	return (
		<>
			<H3>Update Contact Details</H3>
			<Spacer size={16} />
			<Text>Update the contact information for both pick-up and drop-off locations.</Text>
			<Spacer size={16} />
			{pickupGroups.map((group, groupIndex) => {
				return <Fragment key={groupIndex}>
					{generateStopTile('pickup', group[0], groupIndex)}
					<Spacer size={16} />
				</Fragment>
			})}
			{/* The drop-off panels take the slots after the pick-up panels, matching the menu order. */}
			{dropoffGroups.map((group, i) => (
				<Fragment key={`dropoff-${i}`}>
					{generateStopTile('dropoff', group[0], pickupGroups.length + i)}
				</Fragment>
			))}
		</>
	);
}
